import * as React from 'react';

import useRouter, { useLocation, useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { redirectToUnifiedCA } from 'bundles/unified-career-academy/components/modules/EnterpriseCADirectorNotConfigurable';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useIsProgramLoading,
  useIsWildcard,
  useProgram,
  useProgramMembership,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import useShowLearnerOnboarding from 'bundles/unified-common/hooks/useShowLearnerOnboarding';
import { CURRENT_TAB_TO_URL_PARAM } from 'bundles/unified-home-common/constants';
import useEnterpriseMiniModalRedirect from 'bundles/unified-home-common/hooks/useEnterpriseMiniModalRedirect';

const EnterpriseHomeRedirectsWrapper = ({ children }: React.PropsWithChildren<{}>) => {
  const router = useRouter();
  const location = useLocation();
  const program = useProgram();
  const isProgramLoading = useIsProgramLoading();
  const { isMembershipLoading, isProgramMember, isInvited, isProgramAdmin } = useProgramMembership();
  const isWildcard = useIsWildcard();
  const features = useEnterpriseProductFeatures();
  const productFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const modalManager = useEnterpriseModalManager();
  const { programSlug } = useParams();

  useShowLearnerOnboarding({
    router,
    userId: user.get()?.id,
    programSlug: program?.metadata.slug,
    isProgramMember,
    isInvited,
    hasEnabledPersonalizedRecommendations: features?.enablePersonalizedRecommendationsForProgram,
    isProductFeaturesLoading: productFeaturesLoading,
    isInDeepBrowseMode: isWildcard,
    isShowingProgramAccessModal: modalManager?.modalToShow === 'PROGRAM_ACCESS',
  });
  useEnterpriseMiniModalRedirect();

  const lastRoutePath = location.pathname.slice(location.pathname.lastIndexOf('/') + 1);

  const isHomepage = lastRoutePath === programSlug;
  const isSkillSetsPage = lastRoutePath === 'skillsets';
  const isRecommendationsPage = lastRoutePath === 'recommendations';
  const isMyLearningPage = lastRoutePath === 'my-learning';

  const { currentTab, ...newLocationQuery } = location?.query ?? {};

  if (productFeaturesLoading || isMembershipLoading || isProgramLoading) {
    return null;
  }

  // If the program is a CA Limited Program, they should not have access to the Home page. Redirect them to the CA page.
  if (isHomepage && features?.isCareerAcademyLimitedCatalogProgram) {
    redirectToUnifiedCA(router, programSlug);
  }

  // If the program has no skillsets, redirect them to the home page.
  const hasTSPs = !!program?.metadata?.linkedTargetSkillProfileIds?.length;
  if (isSkillSetsPage && program && !hasTSPs) {
    redirectClientOrServer(`/programs/${programSlug}`);
  }

  // If the program does not allow showing recommendations, redirect them to the home page.
  if (
    isRecommendationsPage &&
    program &&
    (!features?.enablePersonalizedRecommendationsForProgram ||
      !user.isAuthenticatedUser() ||
      !isWildcard ||
      !isProgramMember)
  ) {
    redirectClientOrServer(`/programs/${programSlug}`);
  }

  // Redirects for when a user comes to program-home with the currentTab query param
  if (currentTab && !!CURRENT_TAB_TO_URL_PARAM[currentTab as keyof typeof CURRENT_TAB_TO_URL_PARAM]) {
    redirectClientOrServer(
      `/programs/${programSlug}/${CURRENT_TAB_TO_URL_PARAM[currentTab as keyof typeof CURRENT_TAB_TO_URL_PARAM]}?` +
        new URLSearchParams({ ...newLocationQuery }).toString()
    );
  }

  // If the user is logged out or not a program member on the my learning page, redirect them to the home page.
  if (isMyLearningPage && (!user.isAuthenticatedUser() || !(isProgramMember || isProgramAdmin))) {
    redirectClientOrServer(`/programs/${programSlug}`);
  }

  return <React.Fragment>{children}</React.Fragment>;
};

export default EnterpriseHomeRedirectsWrapper;
